// Service detail with commission rate, photos, approve/reject
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { GarageServiceOfferingResponse } from '../types';
import { useAuth } from '../hooks/useAuth';

function OfferingStatusBadge({ status }: { status: string }) {
  const cls = status === 'ACTIVE' ? 'approved'
    : status === 'INACTIVE' ? 'rejected'
    : status === 'REJECTED' ? 'rejected'
    : status === 'PENDING_APPROVAL' ? 'pending'
    : 'pending';
  const label = status === 'PENDING_APPROVAL' ? 'PENDING APPROVAL' : status;
  return <span className={`admin-badge ${cls}`}>{label}</span>;
}

const formatDuration = (minutes: number) => {
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  if (days > 0 && hours > 0) return `${days} days ${hours} hours`;
  if (days > 0) return `${days} days`;
  if (hours > 0) return `${hours} hours`;
  return '0 hours';
};

export default function GarageServiceDetailPage() {
  const auth = useAuth();
  const { garageId } = useParams<{ garageId: string }>();
  const navigate = useNavigate();
  const [offerings, setOfferings] = useState<GarageServiceOfferingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Reject modal state
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Detail modal state
  const [detailOffering, setDetailOffering] = useState<GarageServiceOfferingResponse | null>(null);
  const [detailRejectReason, setDetailRejectReason] = useState('');
  const [showDetailReject, setShowDetailReject] = useState(false);

  const fetchOfferings = useCallback(() => {
    if (!auth.token || !garageId) return;
    setLoading(true);
    setError(null);
    api.getGarageServices(garageId, auth.token)
      .then(setOfferings)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, garageId]);

  useEffect(() => {
    fetchOfferings();
  }, [fetchOfferings]);

  const openDetail = (offering: GarageServiceOfferingResponse) => {
    setDetailOffering(offering);
    setShowDetailReject(false);
    setDetailRejectReason('');
  };

  const closeDetail = () => {
    setDetailOffering(null);
    setShowDetailReject(false);
    setDetailRejectReason('');
  };

  const handleApprove = async (offeringId: string) => {
    if (!auth.token) return;
    setActionLoading(offeringId);
    setError(null);
    try {
      await api.approveOffering(offeringId, auth.token);
      closeDetail();
      fetchOfferings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (offeringId?: string, reason?: string) => {
    const targetId = offeringId || rejectingId;
    const targetReason = reason || rejectReason;
    if (!auth.token || !targetId || targetReason.length < 10) return;
    setActionLoading(targetId);
    setError(null);
    try {
      await api.rejectOffering(targetId, targetReason, auth.token);
      setRejectingId(null);
      setRejectReason('');
      closeDetail();
      fetchOfferings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeStatus = async (offeringId: string, status: 'ACTIVE' | 'INACTIVE') => {
    if (!auth.token) return;
    setActionLoading(offeringId);
    setError(null);
    try {
      await api.changeOfferingStatus(offeringId, status, auth.token);
      closeDetail();
      fetchOfferings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      <div className="admin-detail-header">
        <h2 className="admin-detail-title">
          Garage Service Offerings
        </h2>
        <button className="admin-btn admin-btn-outline" onClick={() => navigate('/service-approvals')}>
          Back to Approvals
        </button>
      </div>

      {error && <div className="admin-info-banner danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Reject Modal (from table) */}
      {rejectingId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => { setRejectingId(null); setRejectReason(''); }}
        >
          <div
            className="admin-card"
            style={{ width: '100%', maxWidth: '480px', margin: '0 1rem' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="admin-card-title" style={{ marginBottom: '0.75rem' }}>Reject Service Offering</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', marginBottom: '0.75rem' }}>
              Provide a reason for rejection (minimum 10 characters).
            </p>
            <textarea
              className="admin-textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              rows={3}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="admin-btn admin-btn-outline"
                onClick={() => { setRejectingId(null); setRejectReason(''); }}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                disabled={rejectReason.length < 10 || actionLoading === rejectingId}
                onClick={() => handleReject()}
              >
                {actionLoading === rejectingId ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailOffering && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto',
          }}
          onClick={closeDetail}
        >
          <div
            className="admin-card"
            style={{ width: '100%', maxWidth: '700px', margin: '2rem 1rem', maxHeight: '90vh', overflow: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, flex: 1 }}>
                {detailOffering.name || detailOffering.category.name}
              </h3>
              <OfferingStatusBadge status={detailOffering.status} />
              <button
                onClick={closeDetail}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--admin-text-muted)', padding: '0.25rem' }}
              >
                &times;
              </button>
            </div>

            {/* Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Category</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{detailOffering.category.parentId ? (detailOffering.category.parentName || '-') : detailOffering.category.name}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Subcategory</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{detailOffering.category.parentId ? detailOffering.category.name : '-'}</p>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Description</label>
                <p style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{detailOffering.description || '-'}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Price (AED)</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{detailOffering.price.toFixed(2)}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Duration</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{formatDuration(detailOffering.durationMinutes)}</p>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Status</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                  <OfferingStatusBadge status={detailOffering.status} />
                </p>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Submitted</label>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>{detailOffering.createdAt ? new Date(detailOffering.createdAt).toLocaleString() : '-'}</p>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Warranty Description</label>
                <p style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{detailOffering.warrantyDescription || '-'}</p>
              </div>
              {detailOffering.status === 'REJECTED' && detailOffering.rejectionReason && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-danger, #c62828)', display: 'block', marginBottom: '0.25rem' }}>Rejection Reason</label>
                  <p style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{detailOffering.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* Images Section */}
            {detailOffering.media && detailOffering.media.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.5rem' }}>Images</label>
                {/* Thumbnail */}
                {detailOffering.media.filter(m => m.mediaType === 'SERVICE_THUMBNAIL').map(media => (
                  <div key={media.mediaId} style={{ marginBottom: '0.75rem' }}>
                    <img
                      src={media.downloadUrl || ''}
                      alt={media.originalFilename}
                      style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--admin-border)' }}
                    />
                    <p style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>Thumbnail</p>
                  </div>
                ))}
                {/* Additional Photos */}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {detailOffering.media.filter(m => m.mediaType === 'SERVICE_PHOTO').map(media => (
                    <div key={media.mediaId}>
                      <img
                        src={media.downloadUrl || ''}
                        alt={media.originalFilename}
                        style={{ width: '120px', height: '90px', borderRadius: '6px', objectFit: 'cover', border: '1px solid var(--admin-border)' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejection Reason textarea (shown on demand) */}
            {showDetailReject && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--admin-text-muted)', display: 'block', marginBottom: '0.25rem' }}>Rejection Reason</label>
                <textarea
                  className="admin-textarea"
                  value={detailRejectReason}
                  onChange={(e) => setDetailRejectReason(e.target.value)}
                  placeholder="Reason for rejection (min 10 characters)..."
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid var(--admin-border)', paddingTop: '1rem' }}>
              {detailOffering.status === 'PENDING_APPROVAL' && (
                <>
                  <button
                    className="admin-btn admin-btn-success"
                    disabled={actionLoading === detailOffering.id}
                    onClick={() => handleApprove(detailOffering.id)}
                  >
                    {actionLoading === detailOffering.id ? 'Approving...' : 'Approve'}
                  </button>
                  {!showDetailReject ? (
                    <button
                      className="admin-btn admin-btn-danger"
                      onClick={() => setShowDetailReject(true)}
                    >
                      Reject
                    </button>
                  ) : (
                    <button
                      className="admin-btn admin-btn-danger"
                      disabled={detailRejectReason.length < 10 || actionLoading === detailOffering.id}
                      onClick={() => handleReject(detailOffering.id, detailRejectReason)}
                    >
                      {actionLoading === detailOffering.id ? 'Rejecting...' : 'Confirm Reject'}
                    </button>
                  )}
                </>
              )}
              {detailOffering.status === 'ACTIVE' && (
                <button
                  className="admin-btn admin-btn-danger"
                  disabled={actionLoading === detailOffering.id}
                  onClick={() => handleChangeStatus(detailOffering.id, 'INACTIVE')}
                >
                  {actionLoading === detailOffering.id ? '...' : 'Deactivate'}
                </button>
              )}
              {detailOffering.status === 'INACTIVE' && (
                <button
                  className="admin-btn admin-btn-success"
                  disabled={actionLoading === detailOffering.id}
                  onClick={() => handleChangeStatus(detailOffering.id, 'ACTIVE')}
                >
                  {actionLoading === detailOffering.id ? '...' : 'Activate'}
                </button>
              )}
              <button className="admin-btn admin-btn-outline" onClick={closeDetail}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : offerings.length === 0 ? (
          <div className="admin-empty-state">
            <p>No service offerings found for this garage.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Price (AED)</th>
                  <th>Duration</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {offerings.map((o) => (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 600 }}>{o.name || o.category.name}</td>
                    <td>{o.price.toFixed(2)}</td>
                    <td>{formatDuration(o.durationMinutes)}</td>
                    <td style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>
                      {o.category.parentId ? `${o.category.parentName ?? '-'} › ${o.category.name}` : o.category.name}
                    </td>
                    <td><OfferingStatusBadge status={o.status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          className="admin-btn admin-btn-outline admin-btn-sm"
                          onClick={() => openDetail(o)}
                        >
                          View Details
                        </button>
                        <button
                          className="admin-btn admin-btn-outline admin-btn-sm"
                          onClick={() => navigate(`/offerings/${o.id}/pending-variants`)}
                          title="Spec 044 v2: review pending variants per car"
                        >
                          Variants
                        </button>
                        {o.status === 'PENDING_APPROVAL' && (
                          <>
                            <button
                              className="admin-btn admin-btn-success admin-btn-sm"
                              disabled={actionLoading === o.id}
                              onClick={() => handleApprove(o.id)}
                            >
                              {actionLoading === o.id ? '...' : 'Approve'}
                            </button>
                            <button
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              disabled={actionLoading === o.id}
                              onClick={() => { setRejectingId(o.id); setRejectReason(''); }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {o.status === 'ACTIVE' && (
                          <button
                            className="admin-btn admin-btn-danger admin-btn-sm"
                            disabled={actionLoading === o.id}
                            onClick={() => handleChangeStatus(o.id, 'INACTIVE')}
                          >
                            {actionLoading === o.id ? '...' : 'Deactivate'}
                          </button>
                        )}
                        {o.status === 'INACTIVE' && (
                          <button
                            className="admin-btn admin-btn-success admin-btn-sm"
                            disabled={actionLoading === o.id}
                            onClick={() => handleChangeStatus(o.id, 'ACTIVE')}
                          >
                            {actionLoading === o.id ? '...' : 'Activate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
